import jApi, {
    DavAddressBookContact,
    WIDGET_LEVEL_1,
    WIDGET_LEVEL_3,
    WidgetContactCardDavType,
    WidgetDisplayMode,
    WidgetWrapperLevel,
} from 'jamespot-user-api';
import { Toast, Widget } from 'jamespot-front-business';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { officeConfigurationInitialState, OfficeConfigurationPrompt } from './components/OfficeConfigurationPrompt';
import { WidgetContactCardDavHeader } from './WidgetContactCardDav/WidgetContactCardDavHeader';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { WidgetScroll } from './components/WidgetScroll';
import { WidgetStylePadding } from './components/WidgetStylePadding';
import { useAppSelector, useAppDispatch } from '../../../redux/store';

const WidgetContactCardDav = commonComponentsRegistry.getLazyComponent('WidgetContactCardDav');

export type JRCWidgetContactCardDavProps = {
    widget: WidgetContactCardDavType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
    levelFallback?: (level: WidgetWrapperLevel, url: string) => ReactNode | undefined;
};

const REFRESH_INTERVAL = 4000;

export const JRCWidgetContactCardDavWrapper = ({ widget, mode, levelFallback }: JRCWidgetContactCardDavProps) => {
    const { uniqid } = widget;
    const dispatch = useAppDispatch();
    const [isConfigurationValid, setIsConfigurationValid] = useState<boolean | undefined>(undefined);
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [doInterval, setDoInterval] = useState<boolean>(false);
    const level = useAppSelector((state) => Widget.selectors.selectWidgetLevel(state, uniqid), shallowEqual);
    const [list, setList] = useState<DavAddressBookContact[]>([]);

    const handleLogout = useCallback(
        (err?: any, reload?: boolean) => {
            if (err && err.error === 400) {
                jApi.office.setConfiguration(officeConfigurationInitialState);
            }
            if (reload) {
                window.location.reload();
                return;
            }

            setIsConfigurationValid(false);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
        },
        [dispatch, uniqid],
    );

    const handleLogin = useCallback(
        (isValid: boolean) => {
            setIsConfigurationValid(isValid);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true }));
        },
        [dispatch, uniqid],
    );

    const handleLoading = useCallback(
        (loading: boolean) => dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading })),
        [uniqid, dispatch],
    );

    const fetchProviderUrls = useCallback(() => {
        jApi.office
            .getProviderBaseUrls()
            .then(({ result }) => {
                setUrl(result.dav_address_book_url);
            })
            .finally(() => handleLoading(false));
    }, [handleLoading]);

    const fetchData = useCallback(() => {
        jApi.office
            .davAddressBookGetContacts({ limit: 10 })
            .then(({ result }) => {
                setList(result);
                setDoInterval(true);
            })
            .catch(() => {
                dispatch(Toast.actions.error({ label: 'PLUGIN_Manager_Connection_Error' }));
                setIsConfigurationValid(false);
                dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
            })
            .finally(() => handleLoading(false));
    }, [handleLoading, dispatch, uniqid]);

    useEffect(() => {
        if (!doInterval) return;
        if (!isConfigurationValid) return;
        if (level !== WIDGET_LEVEL_3) return;
        const interval = setInterval(fetchData, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [isConfigurationValid, fetchData, doInterval, level]);

    useEffect(() => {
        handleLoading(true);
        jApi.office
            .isConfigurationValid()
            .then(({ result }) => handleLogin(result))
            .catch(handleLogout)
            .finally(() => handleLoading(false));
    }, [handleLogin, level, fetchProviderUrls, handleLoading, handleLogout]);

    useEffect(() => {
        if (!isConfigurationValid) return;
        if (level === WIDGET_LEVEL_1) {
            handleLoading(true);
            fetchProviderUrls();
            return;
        }
        fetchData();
    }, [isConfigurationValid, fetchProviderUrls, handleLoading, fetchData, level]);

    if (isConfigurationValid && level && level !== WIDGET_LEVEL_3 && levelFallback) {
        return <>{levelFallback(level, url ?? '')}</>;
    }

    return (
        <WidgetScroll
            header={() => {
                return (
                    <>
                        <WidgetContactCardDavHeader
                            uniqid={uniqid}
                            isConfigurationValid={isConfigurationValid !== undefined && isConfigurationValid === true}
                            handleLogout={() => handleLogout(undefined, true)}
                        />
                    </>
                );
            }}
            scrollable={() => {
                return (
                    <WidgetStylePadding uniqid={uniqid}>
                        {isConfigurationValid && <WidgetContactCardDav contactCardList={list} />}
                        {isConfigurationValid === undefined && <></>}
                        {isConfigurationValid !== undefined && isConfigurationValid === false && mode === 'view' && (
                            <OfficeConfigurationPrompt />
                        )}
                    </WidgetStylePadding>
                );
            }}
        />
    );
};
